import React, { useRef, useEffect } from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMessages } from './hooks/useMessages';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTranslations } from "@/hooks/useTranslations";
import { useAuth } from '@/components/AuthProvider';
import { MessageSquare as MessageSquareIcon } from 'lucide-react';

type MessagesListProps = {
  channelId?: string;
  recipientId?: string;
};

export const MessagesList = ({ channelId, recipientId }: MessagesListProps) => {
  const { messages, loading } = useMessages(channelId, recipientId);
  const { t } = useTranslations('messages');
  const { user } = useAuth();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => scrollToBottom(), 100);
    return () => clearTimeout(timer);
  }, [channelId, recipientId]);
  
  if (loading) {
    return (
      <div className="flex-1 p-4 flex items-center justify-center">
        <div className="text-center text-muted-foreground text-sm">{t('loading')}</div>
      </div>
    );
  }

  if (!channelId && !recipientId) {
    return (
      <div className="flex-1 p-8 flex items-center justify-center">
        <div className="text-center space-y-3">
          <MessageSquareIcon className="h-10 w-10 text-muted-foreground/30 mx-auto" />
          <p className="text-muted-foreground text-sm">{t('selectConversation')}</p>
        </div>
      </div>
    );
  }
  
  if (messages.length === 0 && !loading) {
    return (
      <div className="flex-1 p-8 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground text-sm">{t('noMessages')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full w-full overflow-hidden relative">
      <ScrollArea className="h-full px-4 py-3" ref={scrollAreaRef}>
        <div className="space-y-3">
          {messages.map((message, index) => {
            const isCurrentUser = message.sender_id === user?.id;
            const initials = message.sender_name 
              ? message.sender_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
              : '?';

            // Show name if different sender from previous message
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showName = !prevMessage || prevMessage.sender_id !== message.sender_id;
            
            return (
              <div
                key={message.id}
                className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} gap-2`}
              >
                {!isCurrentUser && (
                  <Avatar className={`h-7 w-7 flex-shrink-0 ${!showName ? 'invisible' : ''}`}>
                    <AvatarImage src="" />
                    <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">{initials}</AvatarFallback>
                  </Avatar>
                )}
                <div className={`flex flex-col max-w-[75%] ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                  {showName && (
                    <div className={`flex items-center gap-1.5 mb-0.5 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                      <span className="font-medium text-xs text-foreground">{message.sender_name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    isCurrentUser 
                      ? 'bg-primary text-primary-foreground rounded-br-md' 
                      : 'bg-muted text-foreground rounded-bl-md'
                  }`}>
                    {message.content}
                  </div>
                  {!showName && (
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
    </div>
  );
};
